import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import {
  AdminTemplateDetails,
  TemplateType,
  deleteAdminTemplate,
  generateAdminTemplateCampaign,
  getAdminTemplateDetails,
} from '../../../services/admin-service';

const typeLabel: Record<TemplateType, string> = {
  image: 'Imagem',
  html: 'E-mail',
  text: 'Texto',
};

const typeColor: Record<TemplateType, 'success' | 'info' | 'secondary'> = {
  image: 'success',
  html: 'info',
  text: 'secondary',
};

const AdminTemplateDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setBreadcrumbs } = useBreadcrumb();
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<AdminTemplateDetails | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [generatingCampaign, setGeneratingCampaign] = useState(false);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Templates', path: '/admin/templates' },
      { label: 'Detalhes do Template', path: id ? `/admin/templates/${id}` : '/admin/templates' },
    ]);
  }, [id, setBreadcrumbs]);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await getAdminTemplateDetails(id);
        if (response) {
          setTemplate(response);
        } else {
          enqueueSnackbar('Falha ao carregar detalhes do template', { variant: 'error' });
        }
      } catch (error) {
        console.error('Erro ao carregar template:', error);
        enqueueSnackbar('Falha ao carregar detalhes do template', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, enqueueSnackbar]);

  const renderedTemplateContent = useMemo(() => {
    if (!template) return null;

    if (template.template_type === 'image') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <img
            src={template.content}
            alt={template.title}
            style={{ maxWidth: '100%', borderRadius: 8 }}
          />
        </Box>
      );
    }

    if (template.template_type === 'html') {
      return <Box dangerouslySetInnerHTML={{ __html: template.content }} />;
    }

    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {template.content}
      </Typography>
    );
  }, [template]);

  const handleDeleteTemplate = async () => {
    if (!id) return;

    setDeleting(true);
    try {
      const response = await deleteAdminTemplate(id);
      if (response?.id) {
        enqueueSnackbar('Template excluído com sucesso', { variant: 'success' });
        navigate('/admin/templates');
      } else {
        enqueueSnackbar('Falha ao excluir template', { variant: 'error' });
      }
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      enqueueSnackbar('Falha ao excluir template', { variant: 'error' });
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleGenerateCampaign = async () => {
    if (!id) return;

    setGeneratingCampaign(true);
    try {
      const response = await generateAdminTemplateCampaign(id);
      if (response?.template_id) {
        navigate(`/admin/campaigns/${response.template_id}/resellers`);
        return;
      }

      enqueueSnackbar('Falha ao gerar campanhas para este template.', { variant: 'error' });
    } catch (error) {
      console.error('Erro ao gerar campanhas:', error);
      enqueueSnackbar('Falha ao gerar campanhas para este template.', { variant: 'error' });
    } finally {
      setGeneratingCampaign(false);
    }
  };

  const triggerDownload = (filename: string, content: BlobPart, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyByType = async () => {
    if (!template) return;

    try {
      if (template.template_type === 'text') {
        await navigator.clipboard.writeText(template.content || '');
        enqueueSnackbar('Texto copiado para a area de transferencia.', { variant: 'success' });
        return;
      }

      if (template.template_type === 'html') {
        await navigator.clipboard.writeText(template.content || '');
        enqueueSnackbar('HTML copiado para a area de transferencia.', { variant: 'success' });
        return;
      }

      // image
      await navigator.clipboard.writeText(template.content);
      enqueueSnackbar('Imagem copiada para a area de transferencia.', { variant: 'success' });
    } catch (error) {
      console.error('Erro ao copiar conteúdo:', error);
      enqueueSnackbar('Falha ao copiar conteúdo para a area de transferencia.', {
        variant: 'error',
      });
    }
  };

  const downloadByType = async () => {
    if (!template) return;

    try {
      const safeName = (template.title || 'template')
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      if (template.template_type === 'text') {
        triggerDownload(
          `${safeName || 'template'}.txt`,
          template.content || '',
          'text/plain;charset=utf-8'
        );
        return;
      }

      if (template.template_type === 'html') {
        triggerDownload(
          `${safeName || 'template'}.html`,
          template.content || '',
          'text/html;charset=utf-8'
        );
        return;
      }

      // image (presigned URL com Content-Disposition: attachment)
      const link = document.createElement('a');
      link.href = template.content_url_for_download || '';
      link.download = `${safeName || 'template'}.png`;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Erro ao baixar conteúdo:', error);
      enqueueSnackbar('Falha ao fazer download do template.', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!template) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Template não encontrado
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/templates')}
          sx={{ mt: 2 }}
        >
          Voltar para Templates
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/templates')}
          disabled={generatingCampaign}
        >
          Voltar para Templates
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleGenerateCampaign}
            disabled={generatingCampaign || deleting}
            startIcon={
              generatingCampaign ? <CircularProgress size={18} color="inherit" /> : undefined
            }
            sx={{
              backgroundColor: 'var(--acer-green, #83b81a)',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#6f9d16',
              },
              '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.7)',
              },
            }}
          >
            {generatingCampaign ? 'Gerando...' : 'Gerar Campanhas'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setExportModalOpen(true)}
            disabled={generatingCampaign}
            sx={{
              borderColor: 'var(--acer-green, #83b81a)',
              color: 'var(--acer-green, #83b81a)',
              backgroundColor: '#fff',
              '&:hover': {
                borderColor: 'var(--acer-green, #83b81a)',
                backgroundColor: 'rgba(131, 184, 26, 0.08)',
              },
            }}
          >
            Exportar Template
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={deleting || generatingCampaign}
          >
            Excluir Template
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5} display="grid" gap={2.5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2">Título</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {template.title}
              </Typography>
              <Typography variant="subtitle2">Descrição</Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto', pr: 1, mb: 2 }}
              >
                {template.description}
              </Typography>
              <Typography variant="subtitle2">Autor</Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto', pr: 1, mb: 2 }}
              >
                {template.author}
              </Typography>
              <Typography variant="subtitle2">Email do Autor</Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto', pr: 1 }}
              >
                {template.author_email}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configurações
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <Typography variant="subtitle2">Tipo de template</Typography>
                  <Chip
                    size="small"
                    label={typeLabel[template.template_type] || template.template_type}
                    color={typeColor[template.template_type] || 'default'}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Typography variant="subtitle2">Público alvo</Typography>
                  <Typography variant="body2">{template.target_audience}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Destaques</Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', maxHeight: 110, overflowY: 'auto', pr: 1 }}
                  >
                    {template.features}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Instruções</Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto', pr: 1 }}
                  >
                    {template.instructions || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Produtos
                  </Typography>
                  {template.products.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Imagem</TableCell>
                          <TableCell>Nome</TableCell>
                          <TableCell>SKU</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {template.products.map(product => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <img
                                src={product.image}
                                alt={product.name}
                                style={{
                                  width: 34,
                                  height: 34,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                }}
                              />
                            </TableCell>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.sku}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum produto vinculado.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Texto de apoio
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2">Título</Typography>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                {template.copywriting_title}
              </Typography>
              <Typography variant="subtitle2">Subtitulo</Typography>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                {template.copywriting_subtitle}
              </Typography>
              <Typography variant="subtitle2">Descrição</Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto', pr: 1 }}
              >
                {template.copywriting_description}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Imagem promocional
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={template.promotional_image}
                  alt="Imagem promocional"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 260,
                    borderRadius: 8,
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Template
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                {renderedTemplateContent}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Excluir Template</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este template?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={deleting || generatingCampaign}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteTemplate}
            disabled={deleting || generatingCampaign}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)}>
        <DialogTitle>Exportar Template</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {template.template_type === 'image'
              ? 'Selecione uma opção para template de imagem.'
              : template.template_type === 'html'
                ? 'Selecione uma opção para template HTML.'
                : 'Selecione uma opção para template de texto.'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {template.template_type !== 'image' && (
              <Button variant="outlined" onClick={copyByType} disabled={generatingCampaign}>
                {template.template_type === 'html' ? 'Copiar HTML' : 'Copiar Texto'}
              </Button>
            )}
            <Button variant="outlined" onClick={downloadByType} disabled={generatingCampaign}>
              Fazer Download
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportModalOpen(false)} disabled={generatingCampaign}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTemplateDetailsPage;
